import React from "react";

// ── colour → hex (RAL-ish approximations; shared with the catalogue seed) ─────
export const COLOUR_HEX = {
  "Black": "#1a1a1a", "Light Grey": "#a9adad", "Dark Grey": "#4b5254", "Graphite": "#373f43",
  "Dark Green": "#015d4b", "Dark Blue": "#20264b", "Sky Blue": "#1e6ca0", "Yellow": "#edc400",
  "Red": "#b81e2c", "Orange": "#dd5713", "Light Ivory": "#e3d3a6",
};
export function hexForColour(name) {
  if (!name) return "";
  const keys = Object.keys(COLOUR_HEX).sort((a, b) => b.length - a.length);
  const hit = keys.find((k) => String(name).toLowerCase().includes(k.toLowerCase()));
  return hit ? COLOUR_HEX[hit] : "";
}

// ── classify an item code into a physical shape ──────────────────────────────
// EC* = corner ramp, ER* = ramp section, EL* = line-marking, everything else
// (E500…, E4x5, E400, AF, ESD tiles) = a square tile.
export function ecotileKind(codeOrItem) {
  const code = String(typeof codeOrItem === "string" ? codeOrItem : (codeOrItem?.item_code || codeOrItem?.code || "")).toUpperCase();
  if (code.startsWith("EC")) return "corner";
  if (code.startsWith("ER")) return "ramp";
  if (code.startsWith("EL")) return "line";
  return "tile";
}

// ── product glyph — shape-correct, colour-filled ─────────────────────────────
// tile = square (raised-disc gets stud dots), ramp = long bar w/ top joints +
// grip ridges, corner = square w/ joints on two sides + slope, line = thin bar.
export function ProductGlyph({ code, hex, tex, kind, colour, size = 20 }) {
  const uid = React.useId().replace(/:/g, "");
  const k = kind || ecotileKind(code);
  const fill = hex || hexForColour(colour) || "";
  const stroke = "rgba(0,0,0,.28)";
  const joint = fill || "#c9cdcc";
  if (!fill && k === "tile") {
    return <span className="rounded-[3px] border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/50 shrink-0 inline-block" style={{ width: size, height: size }} aria-hidden="true" />;
  }
  const common = { width: size, height: size, viewBox: "0 0 24 24", className: "shrink-0", "aria-hidden": true };

  if (k === "ramp") {
    return (
      <svg {...common}>
        {/* top joints */}
        {[5, 11, 17].map((x) => <rect key={x} x={x} y="5.5" width="3" height="3.2" rx="1" fill={joint} stroke={stroke} strokeWidth="0.6" />)}
        {/* body */}
        <rect x="2" y="8.5" width="20" height="9" rx="1.6" fill={fill} stroke={stroke} strokeWidth="0.8" />
        {/* grip ridges (lower half) */}
        {[5, 8, 11, 14, 17, 20].map((x) => <line key={x} x1={x} y1="12.5" x2={x} y2="16.5" stroke="rgba(255,255,255,.35)" strokeWidth="0.8" />)}
      </svg>
    );
  }
  if (k === "corner") {
    return (
      <svg {...common}>
        {/* joints on top + left */}
        {[9.5, 15].map((x) => <rect key={"t" + x} x={x} y="4" width="3" height="3.2" rx="1" fill={joint} stroke={stroke} strokeWidth="0.6" />)}
        {[9.5, 15].map((y) => <rect key={"l" + y} x="4" y={y} width="3.2" height="3" rx="1" fill={joint} stroke={stroke} strokeWidth="0.6" />)}
        {/* body */}
        <rect x="7" y="7" width="14" height="14" rx="1.6" fill={fill} stroke={stroke} strokeWidth="0.8" />
        {/* corner slope ridges */}
        {[3, 6, 9].map((d) => <line key={d} x1={7 + d} y1="21" x2="21" y2={7 + d} stroke="rgba(255,255,255,.32)" strokeWidth="0.8" />)}
      </svg>
    );
  }
  if (k === "line") {
    // line-marking strip: interlocking lugs on BOTH long edges (matches the
    // real product — a narrow plank with dovetail teeth above and below).
    return (
      <svg {...common}>
        {[4.5, 10.5, 16.5].map((x) => <rect key={"t" + x} x={x} y="6.4" width="3" height="2.8" rx="1" fill={joint} stroke={stroke} strokeWidth="0.6" />)}
        <rect x="2" y="8.9" width="20" height="6.2" rx="1.3" fill={fill} stroke={stroke} strokeWidth="0.8" />
        {[7.5, 13.5, 19.5].map((x) => <rect key={"b" + x} x={x} y="14.8" width="3" height="2.8" rx="1" fill={joint} stroke={stroke} strokeWidth="0.6" />)}
      </svg>
    );
  }
  // tile
  return (
    <svg {...common}>
      {tex === "Raised disc" && (
        <defs>
          <pattern id={"d" + uid} width="5" height="5" patternUnits="userSpaceOnUse">
            <circle cx="2.5" cy="2.5" r="0.95" fill="rgba(255,255,255,.5)" />
          </pattern>
        </defs>
      )}
      <rect x="3" y="3" width="18" height="18" rx="2.2" fill={fill} stroke={stroke} strokeWidth="0.8" />
      {tex === "Raised disc" && <rect x="3" y="3" width="18" height="18" rx="2.2" fill={"url(#d" + uid + ")"} />}
    </svg>
  );
}

// ── PVC-granule sack icon (RM store) ─────────────────────────────────────────
// A tied sack with a granule hint — reads as "bag of raw material".
export function PvcBagIcon({ className = "", size }) {
  const s = size || undefined;
  return (
    <svg viewBox="0 0 24 24" width={s} height={s} className={className} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 6.5c0-1.4 1.5-2.5 4-2.5s4 1.1 4 2.5" />
      <path d="M6.8 7h10.4l1 2.2c.2.5.3 1 .3 1.5V19a2 2 0 0 1-2 2H7.5a2 2 0 0 1-2-2v-8.3c0-.5.1-1 .3-1.5L6.8 7Z" />
      <path d="M9.5 12.5h5" />
      <circle cx="10" cy="16" r="0.5" fill="currentColor" stroke="none" />
      <circle cx="12.4" cy="17" r="0.5" fill="currentColor" stroke="none" />
      <circle cx="14" cy="15.5" r="0.5" fill="currentColor" stroke="none" />
    </svg>
  );
}
